/**
 * Color Schemes for Wheel Configuration
 * Phase 7.0: Preset color palettes for beautiful wheels
 */

export interface ColorScheme {
  name: string;
  colors: string[];
  description: string;
}

export const COLOR_SCHEMES: ColorScheme[] = [
  {
    name: 'Ocean',
    colors: ['#0077BE', '#00A8E8', '#00C9FF', '#40E0D0', '#48D1CC', '#5F9EA0'],
    description: 'Cool blues and teals'
  },
  {
    name: 'Sunset',
    colors: ['#FF6B6B', '#FF8E53', '#FFA94D', '#FFD93D', '#F9CA24', '#F0932B'],
    description: 'Warm oranges and yellows'
  },
  {
    name: 'Neon',
    colors: ['#FF006E', '#8338EC', '#3A86FF', '#00F5FF', '#06FFA5', '#FFBE0B'],
    description: 'Vibrant electric colors'
  },
  {
    name: 'Candy',
    colors: ['#FF6B9D', '#FFA5D8', '#C49BFF', '#A5D8FF', '#A8E6CF', '#FFE66D'],
    description: 'Sweet pastel tones'
  },
  {
    name: 'Mono',
    colors: ['#2C3E50', '#34495E', '#7F8C8D', '#95A5A6', '#BDC3C7', '#ECF0F1'],
    description: 'Grayscale elegance'
  },
  {
    name: 'Shopify',
    colors: ['#95BF47', '#5E8E3E', '#7AB55C', '#008060', '#00A97F', '#BBE5B3'],
    description: 'Shopify brand colors'
  },
  {
    name: 'Festival',
    colors: ['#E63946', '#F77F00', '#FCBF49', '#06D6A0', '#118AB2', '#073B4C'],
    description: 'Vibrant celebration'
  },
  {
    name: 'Royal',
    colors: ['#4A0E4E', '#6A0572', '#9B59B6', '#C39BD3', '#E8DAEF', '#F4ECF7'],
    description: 'Rich purples'
  }
];

/**
 * Apply color scheme to segments
 * Cycles through scheme colors if more segments than colors
 */
export function applyColorScheme(
  segments: { outcomeKey: string; label: string; weight: number }[],
  scheme: ColorScheme
): { outcomeKey: string; label: string; color: string; weight: number }[] {
  return segments.map((segment, index) => ({
    ...segment,
    color: scheme.colors[index % scheme.colors.length]
  }));
}

/**
 * Generate random harmonious color (not truly random, picks from curated list)
 */
export function generateNiceColor(): string {
  const niceColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B400', '#52B788',
    '#FF6F91', '#C06C84', '#6C5B7B', '#355C7D', '#A8E6CF'
  ];
  return niceColors[Math.floor(Math.random() * niceColors.length)];
}

/**
 * Validate hex color
 */
export function isValidHexColor(color: string): boolean {
  return /^#[0-9A-F]{6}$/i.test(color);
}

/**
 * Normalize weights to maintain proportions
 */
export function normalizeWeights<T extends { weight: number }>(
  segments: T[]
): T[] {
  const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
  const targetTotal = segments.length; // Normalize to simple integers
  
  return segments.map(seg => ({
    ...seg,
    weight: Math.max(1, Math.round((seg.weight / totalWeight) * targetTotal))
  }));
}
